// hooks/useTrainHandler.ts
import { ITrainData } from "@types";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { checkProjectAIStatus, formatDateInSSE } from "../utils/utils";
import { editProject, startTrainProject } from "../services/projectService";
import { useRecoilState } from "recoil";
import { BASE_URL } from "../services/client";
import { trainStatusState } from "../recoil";
import { getAIInfo } from "../services/serverService";
import { getLatestInspStatus } from "../services/evaluationService";
import { stopTrain } from "../services/trainService";
import { useTrainingStore } from "../stores/training.store";

interface UseTrainHandlerProps {
  id: string | number | undefined;
  step: string;
  label?: string;
  projectDataStep?: string;
  t: (key: string) => string;
  // isTraining?: boolean;
  // setIsTraining?: (val: boolean) => void;
  onTrainingStart?: () => void;
  isTrainable: boolean;
}

export const useTrainHandler = ({
  id,
  step,
  label,
  projectDataStep,
  t,
  // onTrainingStart,
  isTrainable,
}: UseTrainHandlerProps) => {
  // const [isTraining, setIsTraining] = useRecoilState(isTrainingState);
  const { setIsTraining } = useTrainingStore();

  const [inspResult, setInspResult] = useState<any>([]);
  const [inspStatus, setInspStatus] = useState<InspStatusItem | null>(null);

  const [trainOption, setTrainOption] = useState<ITrainData | null>(null);
  const [openSelectTrain, setOpenSelectTrain] = useState(false);
  const [aiServer, setAiServer] = useState<any>(null);

  const [trainStatus, setTrainStatus] = useRecoilState<trainStatusResponse>(trainStatusState);
  const eventSourceRef = useRef<EventSource | null>(null);

  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1000;

  const handleTrain = async () => {
    if (!id) return;
    if (!isTrainable) {
      toast.warn("NG개수가 학습 실행 조건에 충족되지 않습니다.");
    } else {
      try {
        const isBusy = await checkProjectAIStatus(Number(id));
        if (isBusy) return;
        await startTrainingRequest();
      } catch (error: any) {
        handleTrainError(error);
      }
    }
  };

  const startTrainingRequest = async () => {
    if (!id) return;

    const stepToUse = projectDataStep || step;
    const labelParam = stepToUse === "분석" ? label || undefined : undefined;
    const isMulti = !labelParam;

    const response = await startTrainProject(Number(id), stepToUse, labelParam, isMulti);

    toast.success(response.message);
    setIsTraining?.(true);
    // onTrainingStart?.();
  };

  const handleTrainError = (error: any) => {
    if (error?.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error instanceof Error) {
      if (error.message === t("MsgTrainModelNotSelected")) {
        setOpenSelectTrain(true);
      }
      toast.warn(error.message);
    } else {
      toast.error("An unknown error occurred.");
    }
    console.error("Error during training:", error);
  };

  const handleTrainChange = (selected: ITrainData | null) => {
    setTrainOption(selected);
  };

  const handleSelectTrain = () => {
    const editTrain = async () => {
      try {
        if (trainOption && id) {
          await editProject(Number(id), {
            train_id: trainOption.id,
          });

          // 수정 성공 시 바로 학습 요청
          await startTrainingRequest();
        }
      } catch (error) {
        console.error(error);
        handleTrainError(error);
      } finally {
        setOpenSelectTrain(false);
      }
    };

    editTrain();
  };

  const handleStopTrain = async () => {
    if (aiServer && aiServer.train.project_id === Number(id)) {
      if (window.confirm("해당 학습을 중단하시겠습니까?")) {
        try {
          const response = await stopTrain(aiServer.train.trainLog_id);
          toast.success(response.msg);
          fetchAIServer();
        } catch (error) {
          toast.error("학습 중단에 실패하였습니다. 다시 시도해주세요.");
          console.error(error);
        }
      }
    }
  };

  const fetchAIServer = async () => {
    // console.log("fetchAIServer 함수 실행");

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // console.log(`getAIInfo 시도 ${attempt}회`);

      try {
        const response = await getAIInfo();

        const hasCanceledServer = response.data.some((item: any) => item.train?.state === "cancel");

        // 학습 취소(서버 오류)
        if (hasCanceledServer) {
          toast.warn("서버 오류로 인해 학습이 취소되었습니다.");
          setIsTraining(false);
          fetchInspResult();
          return; // 함수 조기 종료
        }

        let matchedQueueItem: any = null;

        for (const item of response.data) {
          const trainData = item.train;
          // 현재 학습중인 데이터와 큐에 있는 데이터가 동일한 경우: 학습이 준비중(pending)에 있음
          const found = item.queue.find((q: any) => q.trainLog_uuid === trainData.trainLog_id);
          if (found) {
            matchedQueueItem = item;
            break;
          }
        }

        let filteredAI: any = null;

        // 해당 학습중 데이터가 현재 프로젝트인 경우
        if (matchedQueueItem) {
          filteredAI = matchedQueueItem;
          setAiServer(filteredAI);
          // TODO: trainHistory의 trainLog_uuid값 업데이트
        } else {
          // else: 해당 프로젝트 학습이 큐에 쌓인 경우
          const aiServer = response.data.filter((item: any) => String(item.train.project_id) === id);
          filteredAI = aiServer[0] ?? null;
          setAiServer(filteredAI);
        }

        //현재 프로젝트에 대한 학습이 진행중인 경우: 학습 상태를 계속 받아옴
        if (filteredAI) {
          const url = `${BASE_URL}/ai-info-server?id=${id}&server=${filteredAI.server}`;
          const eventSource = new EventSource(url);
          eventSourceRef.current = eventSource;
          eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);
            // console.log(data);
            setTrainStatus(data);
            setIsTraining(true);
            if (data.state === "cancel") {
              toast.warn("서버 오류로 인해 학습이 취소되었습니다.");
              setIsTraining(false);
              eventSource?.close();

              setTimeout(() => {
                console.log("검사조회");
                fetchInspResult();
              }, 5000);
              return; // cancel이면 여기서 종료
            }

            if (data.state === "none" || data.state === "end") {
              // console.log("FetchAIServer Close SSE connection");
              setIsTraining(false);
              eventSource?.close();

              setTimeout(() => {
                console.log("검사조회");
                fetchInspResult();
              }, 5000);
            }
          };

          eventSource.onerror = (error) => {
            console.error("SSE 오류:", error);
            eventSource?.close();
          };

          break; // SSE 연결했으니 반복 종료
        } else {
          // console.log("filteredAI 조건 불충족, 다음 시도 대기...");
          await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        }

        if (attempt === MAX_RETRIES) {
          // console.warn("최대 시도 내 filteredAI를 찾지 못했습니다.");
          setIsTraining(false);
          fetchInspResult();
        }
      } catch (error) {
        console.error("fetchAIServer error:", error);
        setIsTraining(false);
        break;
      }
    }
  };

  const fetchInspResult = async () => {
    let eventSource: EventSource | null = null;
    const latestInspStatus = await getLatestInspStatus(Number(id));
    const data = latestInspStatus.data;
    setInspStatus(data);

    if (data && data.state !== "end") {
      const url = `${BASE_URL}/ai-label/${id}?create_at=${formatDateInSSE(
        data.create_at,
      )}&update_at=${formatDateInSSE(data.update_at)}`;
      eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setInspResult(data.data);
        setInspStatus(data.status);
        // PROD: 검사가 완료(status가 end 또는 none)된 경우
        if (data.status?.state === "end" || data.status?.state === "cancel") {
          console.log("FetchInspResult Close SSE connection");
          eventSource?.close();
        }
      };

      eventSource.onerror = (error) => {
        console.error("SSE 오류:", error);
        eventSource?.close();
      };
    }
  };

  return {
    handleTrain,
    handleTrainChange,
    handleSelectTrain,
    handleStopTrain,
    trainOption,
    openSelectTrain,
    fetchAIServer,
    setOpenSelectTrain,
    fetchInspResult,
    inspResult,
    inspStatus,
  };
};
